import { Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { ON_TEAL_TEXT } from '../constants';
import type { CandidateVM, ModelPillVM } from '../types';
import { SignalAnalystHeader } from '../components/signal-analyst-header';
import { HeroPromptBar } from '../components/hero-prompt-bar';
import { CandidateCard } from '../components/candidate-card';
import { ModelSelector } from '../components/model-selector';
import { SpecsDropzone } from '../components/specs-dropzone';

export interface SetupSectionProps {
  brandName: string;

  productQuery: string;
  onChangeProductQuery: (v: string) => void;
  selectedProductName: string | null;
  onSelectProductName: (name: string) => void;

  candidates: CandidateVM[];
  totalProducts: number;
  /** true while the brand's detected products are still being fetched. */
  candidatesLoading?: boolean;

  onViewAllProducts?: () => void;

  modelPills: ModelPillVM[];
  recommendedCount: number;
  onRemoveModel: (id: string) => void;
  onUseRecommended: () => void;
  onOpenModelModal: () => void;

  specsOpen: boolean;
  onToggleSpecs: () => void;
  uploadedFile: File | null;
  onUploadFile: (file: File) => void;
  onRemoveFile: () => void;

  onRun: () => void;
  canRun: boolean;
  runLoading: boolean;
}

// The luminous setup panel (DC §4): pinned at the top of the Analysis tab. It
// never gates the page; the run output always renders below it.
export function SetupSection(props: SetupSectionProps) {
  const {
    brandName,
    productQuery,
    onChangeProductQuery,
    candidates,
    totalProducts,
    candidatesLoading,
    onSelectProductName,
    onViewAllProducts,
    modelPills,
    recommendedCount,
    onRemoveModel,
    onUseRecommended,
    onOpenModelModal,
    specsOpen,
    onToggleSpecs,
    uploadedFile,
    onUploadFile,
    onRemoveFile,
    onRun,
    canRun,
    runLoading,
  } = props;

  const runDisabled = !canRun || runLoading;

  return (
    <div
      style={{
        position: 'relative',
        background:
          'linear-gradient(135deg,var(--spectral-violet-10),var(--alpha-teal-5) 56%,var(--card))',
        border: '1px solid var(--border)',
        borderRadius: 16,
        boxShadow: '0 1px 2px rgba(13,13,15,.04),0 18px 46px -22px rgba(126,95,224,.36)',
        overflow: 'hidden',
        marginBottom: 26,
      }}
    >
      {/* corner aura */}
      <div
        style={{
          position: 'absolute',
          top: '-32%',
          right: '-5%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle,var(--spectral-violet-10),transparent 66%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', padding: '30px 32px 22px' }}>
        <SignalAnalystHeader brandName={brandName} />

        <HeroPromptBar
          value={productQuery}
          onChange={onChangeProductQuery}
          onSubmit={() => !runDisabled && onRun()}
          disabled={runDisabled}
        />

        {/* candidates — loading line during first fetch (no empty placeholders) */}
        {candidatesLoading && candidates.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Loader2
              style={{ width: 13, height: 13, color: 'var(--spectral-violet)', flexShrink: 0 }}
              className="animate-spin"
            />
            <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
              Detecting products for {brandName}…
            </span>
          </div>
        )}

        {/* candidates */}
        {candidates.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 13 }}>
              <Sparkles style={{ width: 13, height: 13, color: 'var(--spectral-violet)', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
                Or pick from the products I detected for {brandName}:
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {candidates.map((c) => (
                <CandidateCard key={c.id} candidate={c} onPick={onSelectProductName} />
              ))}
            </div>
            {totalProducts > candidates.length && (
              <button
                type="button"
                onClick={onViewAllProducts}
                style={{
                  marginTop: 12,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--signal-blue)',
                  cursor: 'pointer',
                }}
              >
                View all {totalProducts} products
              </button>
            )}
          </>
        )}

        {/* Model chips and the Analyze CTA share one row: the button belongs
            beside the thing it acts on, not stranded under a tall block of
            padding at the bottom of the card (UPM-147). */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <ModelSelector
              pills={modelPills}
              recommendedCount={recommendedCount}
              onRemove={onRemoveModel}
              onUseRecommended={onUseRecommended}
              onOpenModelModal={onOpenModelModal}
            />
          </div>
          <button
            type="button"
            onClick={() => !runDisabled && onRun()}
            disabled={runDisabled}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              height: 44,
              padding: '0 22px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--alpha-teal)',
              color: ON_TEAL_TEXT,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: runDisabled ? 'not-allowed' : 'pointer',
              opacity: runDisabled ? 0.6 : 1,
              boxShadow: '0 1px 2px rgba(0,200,170,.25)',
              transition: 'filter var(--duration-base) var(--ease-standard)',
            }}
            onMouseEnter={(e) => {
              if (!runDisabled) e.currentTarget.style.filter = 'brightness(1.04)';
            }}
            onMouseLeave={(e) => (e.currentTarget.style.filter = 'none')}
          >
            {runLoading ? (
              <>
                <Loader2 style={{ width: 15, height: 15 }} className="animate-spin" />
                Analyzing…
              </>
            ) : (
              <>
                Analyze
                <ArrowRight style={{ width: 15, height: 15 }} />
              </>
            )}
          </button>
        </div>

        <SpecsDropzone
          open={specsOpen}
          onToggle={onToggleSpecs}
          uploadedFile={uploadedFile}
          onUpload={onUploadFile}
          onRemove={onRemoveFile}
        />
      </div>
    </div>
  );
}
